import React from 'react';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { DepositDoc, DocAccessType, IDocumentsList } from '../../../../types';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { formatCurrency } from '../../../../utilities/formatting';
import { UserRole } from '../../../../store/auth/types';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  document: DepositDoc;
  depositAmount?: number;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  canAcknowledge: boolean;
  isDeleting: boolean;
  onResume: (url: string) => void;
  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const Deposit = (props: IProps) => {
    const { t } = useTranslation('common');
  const { document: deposit, infoOnly, userAccess, userRole } = props;

  const hasCopyOfDepositReceipt = deposit?.hasCopyOfDepositReceipt;

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  if (deposit) {
    const visibleDocs = getVisible({ documentsHash: deposit, userAccess, infoOnly, userRole }) as IDocumentsList[];
    if (hasDeleted(deposit)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          return (
            <FingerWithApprove
              key={`${key}-deposit-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:deposit2', { index: Object.keys(deposit).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick(key)}
              onOpenClick={() => props.onOpenClick(key)}
              onApproveClick={() => props.onApproveClick(key)}
              onRejectClick={() => props.onRejectClick(key)}
              onRescindRejectClick={() => props.onRescindRejectClick(key)}
              onDeleteClick={() => props.onDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              <>
                {(hasCopyOfDepositReceipt === false ||
                  (item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0)) && (
                  <>
                    {hasCopyOfDepositReceipt === undefined ? (
                      <div>{t('common:uploadedBy')} {deposit[key].uploadedBy ? deposit[key].uploadedBy : 'University'}</div>
                    ) : (
                      <SummaryRow
                        key="hasCopyOfDepositReceipt"
                        label={t('common:hasDepositReceipt')}
                        fullLabel="Are you able to get a copy of your deposit receipt?"
                        value={hasCopyOfDepositReceipt ? 'Yes' : 'No'}
                      />
                    )}
                  </>
                )}
              </>
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={props.id}
      label={t('common:deposit')}
      amount={`£${formatCurrency(props.depositAmount ?? 0)}`}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('finances/step-1');
      }}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default Deposit;
